<script setup lang="ts">
import { computed, ref } from 'vue';
import { useLocale } from '@/plugins/translation';
import type { Exercise, Instruction } from '@ilteducation/exercise-service-types';
import chalkboard from '@ilteducation/games/src/quizter/assets/chalkboard.webp';

const props = defineProps<{
  exercise: Exercise;
  title: string;
}>();

const emit = defineEmits<{
  close: [];
}>();

const { t, l } = useLocale();

const questions = computed(() => props.exercise.content as Instruction[]);
const currentIndex = ref(0);
const showList = ref(false);
</script>
<template>
  <v-card
    theme="baseDark"
    :image="chalkboard"
    color="surface"
    variant="flat"
    rounded="0"
    class="d-flex flex-column min-h-100 pa-4"
  >
    <div class="d-flex align-center justify-space-between flex-wrap ga-4">
      <div class="d-flex align-center ga-2">
        <v-btn
          icon="fa:far fa-chevron-left"
          variant="text"
          color="on-surface"
          :aria-label="t(l.label_go_back)"
          @click="emit('close')"
        />
        <h1 id="question_display_title" class="chalkduster-font text-h5 text-sm-h4 font-weight-regular ma-0">
          {{ title }}
        </h1>
      </div>
      <v-btn
        variant="outlined"
        color="on-surface"
        height="48"
        class="text-none w-100 w-sm-auto mr-sm-16"
        @click="showList = !showList"
      >
        {{ showList ? t(l.reflection_questions.show_one_at_a_time) : t(l.reflection_questions.show_as_list) }}
        <template #append>
          <v-icon icon="fa:far fa-list-ul" class="ml-4" />
        </template>
      </v-btn>
    </div>

    <v-list v-if="showList" class="d-flex flex-column ga-12 bg-transparent px-sm-16 py-12">
      <v-list-item v-for="(question, index) in questions" :key="index" class="chalkboard-font text-h5 text-sm-h4 pa-0">
        {{ question.description }}
      </v-list-item>
    </v-list>

    <v-container v-else max-width="940" class="d-flex flex-column justify-sm-center flex-grow-1 ga-8 px-0 py-8">
      <v-card
        variant="flat"
        color="rgba(0, 0, 0, 0.5)"
        min-height="300"
        class="border-md border-opacity-100 rounded-lg pa-4 pa-sm-8"
      >
        <p class="chalkboard-font text-h5 text-sm-h4">{{ questions[currentIndex].description }}</p>
      </v-card>

      <div class="d-flex align-center justify-space-between flex-wrap ga-4">
        <span class="text-subtitle-1">
          {{ t(l.quiz.quiz_progress, { current: currentIndex + 1, last: questions.length }) }}
        </span>
        <div class="d-flex ga-4 ms-auto">
          <v-btn
            variant="outlined"
            color="on-surface"
            height="48"
            class="text-none"
            prepend-icon="fa:far fa-chevron-left"
            :disabled="currentIndex === 0"
            @click="currentIndex--"
          >
            {{ t(l.quiz.back) }}
          </v-btn>
          <v-btn
            variant="flat"
            color="inverse-surface"
            height="48"
            class="text-none"
            append-icon="fa:far fa-chevron-right"
            :disabled="currentIndex === questions.length - 1"
            @click="currentIndex++"
          >
            {{ t(l.quiz.next) }}
          </v-btn>
        </div>
      </div>
    </v-container>
  </v-card>
</template>

<style lang="scss" scoped>
@font-face {
  font-family: 'Chalkduster';
  src: url('@/assets/fonts/Chalkduster.ttf') format('truetype');
  font-weight: normal;
  font-style: normal;
}

@font-face {
  font-family: 'Chalkboard';
  src: url('@ilteducation/games/src/quizter/assets/Chalkboard Regular.ttf') format('truetype');
  font-weight: normal;
  font-style: normal;
}

.chalkduster-font {
  font-family: 'Chalkduster', sans-serif !important;
}

.chalkboard-font {
  font-family: 'Chalkboard', sans-serif !important;
}
</style>
